/**
 * SettingsPage — responsive: mobile / tablet / desktop.
 * Layer: C (UX/CLI)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { settings, type ProviderKeyInfo, type McpServerInfo, type RoutingConfig } from '../lib/api';

const PROVIDERS = ['anthropic', 'openai', 'google', 'perplexity', 'ollama'] as const;

export function SettingsPage() {
  const navigate = useNavigate();
  return (
    <div className="h-full overflow-y-auto overscroll-contain scrollbar-thin">
      <div className="mx-auto max-w-2xl space-y-6 md:space-y-8 p-4 sm:p-5 md:p-6 lg:p-8">
        <div className="flex items-center gap-3 md:gap-4">
          <button onClick={() => navigate('/chat')}
            className="flex h-[40px] items-center rounded-lg px-3 text-sm text-accent
              hover:text-accent-hover hover:bg-accent/5 transition-colors touch-manipulation active:scale-95">
            ← Tillbaka
          </button>
          <h1 className="text-lg md:text-xl font-bold">Inställningar</h1>
        </div>
        <KeyManager />
        <McpManager />
        <RoutingManager />
      </div>
    </div>
  );
}

// ── Shared input class ────────────────────────────────────────────────
const inputCls = "w-full rounded-lg border border-border bg-bg-tertiary px-3 py-2.5 md:py-2 text-[15px] sm:text-[15px] md:text-[14px] focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20 transition-colors";
const btnCls = "rounded-lg bg-accent px-4 py-2.5 md:py-2 text-[15px] md:text-[14px] text-white font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors active:scale-[0.98] touch-manipulation";
const sectionCls = "rounded-xl border border-border bg-bg-secondary p-4 sm:p-5";

// ── BYO API Keys ──────────────────────────────────────────────────────

function KeyManager() {
  const [keys, setKeys] = useState<ProviderKeyInfo[]>([]);
  const [provider, setProvider] = useState<string>('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { settings.keys.list().then(d => setKeys(d.keys)).catch(() => {}); }, []);

  async function handleAdd() {
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      await settings.keys.add(provider, apiKey.trim(), baseUrl.trim() || undefined);
      setApiKey(''); setBaseUrl('');
      const d = await settings.keys.list(); setKeys(d.keys);
    } catch {}
    setSaving(false);
  }

  async function handleRemove(p: string) {
    await settings.keys.remove(p);
    setKeys(keys.filter(k => k.provider !== p));
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-3 md:mb-4 text-[16px] md:text-base font-semibold">API-nycklar (BYO-Key)</h2>
      {keys.length > 0 && (
        <div className="mb-4 space-y-2">
          {keys.map(k => (
            <div key={k.provider} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2
              rounded-lg bg-bg-tertiary px-3 py-2.5 text-[15px] md:text-[14px]">
              <span className="font-medium capitalize">{k.provider}</span>
              <div className="flex items-center gap-3 text-xs text-text-secondary">
                <span>{k.last_used_at ? `Senast: ${new Date(k.last_used_at).toLocaleDateString('sv')}` : 'Aldrig använd'}</span>
                <button onClick={() => handleRemove(k.provider)}
                  className="text-error hover:underline min-h-[44px] sm:min-h-0 flex items-center touch-manipulation">Ta bort</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <select value={provider} onChange={e => setProvider(e.target.value)}
            className={`${inputCls} sm:w-40`}>
            {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="API-nyckel"
            className={`${inputCls} flex-1`} />
        </div>
        {(provider === 'openai' || provider === 'ollama') && (
          <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} placeholder="Base URL (valfritt)"
            className={inputCls} />
        )}
        <button onClick={handleAdd} disabled={saving || !apiKey.trim()} className={btnCls}>
          {saving ? 'Sparar...' : 'Lägg till nyckel'}
        </button>
      </div>
    </section>
  );
}

// ── MCP Servers ───────────────────────────────────────────────────────

function McpManager() {
  const [servers, setServers] = useState<McpServerInfo[]>([]);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => { settings.mcp.list().then(d => setServers(d.servers)).catch(() => {}); }, []);

  async function handleAdd() {
    if (!name.trim() || !url.trim()) return;
    await settings.mcp.add(name.trim(), url.trim());
    setName(''); setUrl('');
    const d = await settings.mcp.list(); setServers(d.servers);
  }

  async function handleRemove(id: string) {
    await settings.mcp.remove(id);
    setServers(servers.filter(s => s.id !== id));
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-3 md:mb-4 text-[16px] md:text-base font-semibold">MCP-servrar</h2>
      {servers.length > 0 && (
        <div className="mb-4 space-y-2">
          {servers.map(s => (
            <div key={s.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2
              rounded-lg bg-bg-tertiary px-3 py-2.5 text-[15px] md:text-[14px]">
              <div className="min-w-0">
                <span className="font-medium">{s.name}</span>
                <span className="ml-2 text-xs text-text-secondary truncate block sm:inline">{s.server_url}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-xs ${s.health_status === 'ok' ? 'text-success' : 'text-warning'}`}>{s.health_status}</span>
                <button onClick={() => handleRemove(s.id)}
                  className="text-xs text-error hover:underline min-h-[44px] sm:min-h-0 flex items-center touch-manipulation">Ta bort</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Namn"
          className={`${inputCls} sm:w-32 md:w-40`} />
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://mcp-server.example.com"
          className={`${inputCls} flex-1`} />
        <button onClick={handleAdd} className={`${btnCls} sm:w-auto`}>Lägg till</button>
      </div>
    </section>
  );
}

// ── Routing Config ────────────────────────────────────────────────────

function RoutingManager() {
  const [config, setConfig] = useState<RoutingConfig | null>(null);

  useEffect(() => { settings.routing.get().then(d => setConfig(d.routing)).catch(() => {}); }, []);

  async function handleSave() {
    if (!config) return;
    const d = await settings.routing.update(config);
    setConfig(d.routing);
  }

  if (!config) return null;

  return (
    <section className={sectionCls}>
      <h2 className="mb-3 md:mb-4 text-[16px] md:text-base font-semibold">Model Routing</h2>
      <div className="space-y-3 md:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label className="text-[15px] md:text-[14px] text-text-secondary sm:w-24 shrink-0">Mode</label>
          <select value={config.mode} onChange={e => setConfig({ ...config, mode: e.target.value as 'auto' | 'manual' })}
            className={`${inputCls} sm:w-40`}>
            <option value="auto">Auto</option>
            <option value="manual">Manuell</option>
          </select>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label className="text-[15px] md:text-[14px] text-text-secondary sm:w-24 shrink-0">Provider</label>
          <select value={config.default_provider} onChange={e => setConfig({ ...config, default_provider: e.target.value })}
            className={`${inputCls} sm:w-40`}>
            {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label className="text-[15px] md:text-[14px] text-text-secondary sm:w-24 shrink-0">Modell</label>
          <input value={config.default_model} onChange={e => setConfig({ ...config, default_model: e.target.value })}
            className={`${inputCls} flex-1`} />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label className="text-[15px] md:text-[14px] text-text-secondary sm:w-24 shrink-0">Budget/mån</label>
          <div className="flex items-center gap-2 flex-1">
            <input type="number" value={config.budget_monthly_usd ?? ''} onChange={e => setConfig({ ...config, budget_monthly_usd: e.target.value ? Number(e.target.value) : null })}
              placeholder="Obegränsad" className={`${inputCls} w-full sm:w-32`} />
            <span className="text-xs text-text-secondary shrink-0">USD</span>
          </div>
        </div>
        <button onClick={handleSave} className={btnCls}>Spara</button>
      </div>
    </section>
  );
}
